import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Crown, MessageCircle, Sparkles, CreditCard, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const ORDER = { starter: 1, business: 2, professional: 3, enterprise: 4 };

export const PlanPickerDialog = ({ open, onOpenChange, subscription }) => {
  const [plans, setPlans] = useState([]);
  const [payingPlan, setPayingPlan] = useState(null);
  const currentPlan = subscription?.plan || null;
  const currentRank = currentPlan ? ORDER[currentPlan] : 0;
  const waNum = subscription?.support_whatsapp || "";

  useEffect(() => {
    if (open && plans.length === 0) api.getPlans().then(setPlans).catch(() => {});
  }, [open]);

  const waLink = (p) =>
    `https://wa.me/${waNum}?text=${encodeURIComponent(`مرحباً، أرغب بالاشتراك/الترقية إلى باقة ${p.name_ar} في WorkZone`)}`;

  const subscribe = async (p) => {
    setPayingPlan(p.id);
    try {
      const data = await api.paymobSubscribe(p.id);
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء الدفع، حاول مرة أخرى");
      setPayingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-2xl font-extrabold text-slate-900">اختر باقتك وادفع مباشرة</DialogTitle>
          <DialogDescription className="text-start">
            الترقية فورية وتُفعّل جميع مزايا الباقة مباشرةً بعد إتمام الدفع.
            {currentPlan && (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5" /> باقتك الحالية: {subscription?.plan_details?.name_ar || currentPlan}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch mt-2">
          {plans.map((p) => {
            const isActive = subscription?.status === "active";
            const isCurrent = p.id === currentPlan;
            const isPopular = p.id === "business";
            const rank = ORDER[p.id] || 0;
            const isDowngrade = currentRank > 0 && rank < currentRank;
            // أثناء التجربة/انتهاء الاشتراك: كل الباقات المسعّرة قابلة للدفع (حتى الحالية لتفعيلها).
            // عند وجود اشتراك فعّال: الحالية معطّلة، والتنزيل عبر واتساب، والترقية بالدفع.
            const payable = p.price != null && (!isActive || (!isCurrent && !isDowngrade));
            return (
              <div key={p.id} data-testid={`plandlg-card-${p.id}`}
                className={`relative rounded-2xl border bg-white p-5 flex flex-col transition ${isCurrent ? "border-emerald-400 ring-2 ring-emerald-400/30" : isPopular ? "border-blue-400 ring-2 ring-blue-400/30 shadow-lg" : "border-slate-200"}`}>
                {isCurrent && (
                  <span className="absolute -top-3 right-5 rounded-full bg-emerald-600 text-white text-xs font-bold px-3 py-1 inline-flex items-center gap-1">
                    <Check className="h-3 w-3" /> باقتك
                  </span>
                )}
                {isPopular && !isCurrent && (
                  <span className="absolute -top-3 right-5 rounded-full bg-blue-600 text-white text-xs font-bold px-3 py-1">الأكثر شيوعاً</span>
                )}
                <div className="flex items-center gap-2">
                  {p.id === "enterprise" && <Crown className="h-5 w-5 text-amber-500" />}
                  <h3 className="font-heading text-lg font-bold text-slate-900">{p.name_ar}</h3>
                </div>
                <div className="mt-2 flex items-end gap-1.5">
                  {p.price != null ? (
                    <>
                      <span className="text-2xl font-extrabold text-slate-900">{p.price}</span>
                      <span className="text-slate-500 text-xs mb-1">{p.currency} / {p.period}</span>
                    </>
                  ) : (
                    <span className="text-xl font-extrabold text-slate-900">{p.period}</span>
                  )}
                </div>

                <ul className="mt-4 space-y-1.5 flex-1">
                  {(p.features || []).slice(0, 6).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5">
                  {payable ? (
                    <Button data-testid={`plandlg-subscribe-${p.id}`} disabled={payingPlan === p.id} onClick={() => subscribe(p)}
                      className={`w-full h-10 gap-1.5 ${isCurrent ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}>
                      {payingPlan === p.id
                        ? <><Loader2 className="h-4 w-4 animate-spin" /> جارٍ فتح الدفع…</>
                        : <><CreditCard className="h-4 w-4" /> {isCurrent ? "اشترك وفعّل" : "اشترك"} — {p.price} {p.currency}</>}
                    </Button>
                  ) : isCurrent ? (
                    <Button disabled className="w-full h-10 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">باقتك الحالية</Button>
                  ) : (
                    <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`plandlg-cta-${p.id}`}>
                      <Button variant="outline" className="w-full h-10 gap-1.5">
                        <MessageCircle className="h-4 w-4" /> {p.id === "enterprise" ? "تواصل مع المبيعات" : "التغيير لهذه الباقة"}
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {waNum && (
          <div className="text-center mt-4">
            <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
              <MessageCircle className="h-4 w-4" /> هل لديك سؤال؟ تواصل معنا على واتساب
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
